import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, LOAN_PRODUCTS, LOAN_TYPE_LABEL, inr } from "@/lib/api";
import { toast } from "sonner";
import { Search, Plus, UserCheck, Download, Upload, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const STATUS_STYLES = {
  enquiry: "bg-slate-100 text-slate-700",
  new: "bg-slate-100 text-slate-700",
  contacted: "bg-amber-100 text-amber-800",
  interested: "bg-blue-100 text-blue-800",
  qualified: "bg-blue-100 text-blue-800",
  callback: "bg-purple-100 text-purple-800",
  converted: "bg-emerald-100 text-emerald-800",
  lost: "bg-red-100 text-red-800",
  not_interested: "bg-red-100 text-red-800",
};

const STATUSES = ["enquiry", "new", "contacted", "interested", "callback", "qualified", "converted", "not_interested", "lost"];

function NewLeadDialog() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", city: "Siddipet", enquiry_type: "loan", loan_type: "personal_loan", insurance_type: "car", loan_amount: "", source: "walk_in", referral_executive_id: "", notes: "" });
  const { data: employees = [] } = useQuery({
    queryKey: ["employees-active"],
    queryFn: async () => { try { return (await api.get("/employees")).data.filter((e) => e.active); } catch { return []; } },
    enabled: open,
  });
  const referredEmp = employees.find((e) => e.id === form.referral_executive_id);
  const mut = useMutation({
    mutationFn: async (v) => (await api.post("/leads", v)).data,
    onSuccess: () => { toast.success("Lead created"); qc.invalidateQueries({ queryKey: ["leads"] }); setOpen(false); setForm({ full_name: "", phone: "", email: "", city: "Siddipet", enquiry_type: "loan", loan_type: "personal_loan", insurance_type: "car", loan_amount: "", source: "walk_in", referral_executive_id: "", notes: "" }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const submit = (e) => {
    e.preventDefault();
    mut.mutate({
      ...form,
      loan_type: form.enquiry_type === "loan" ? form.loan_type : null,
      insurance_type: form.enquiry_type === "insurance" ? form.insurance_type : null,
      loan_amount: form.loan_amount ? Number(form.loan_amount) : null,
      email: form.email || null,
      referral_executive_id: form.source === "referral" ? (form.referral_executive_id || null) : null,
    });
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="bg-[#0052FF] hover:bg-[#003FCC] text-white text-sm font-semibold px-4 py-2 rounded-full inline-flex items-center gap-1.5" data-testid="new-lead-btn">
          <Plus size={14} /> New Lead
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader><DialogTitle>New Lead</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2" data-testid="new-lead-form">
          <div>
            <Label>Enquiry type</Label>
            <div className="mt-1 grid grid-cols-3 gap-2">
              <label className={`border rounded-lg p-2 text-sm cursor-pointer text-center ${form.enquiry_type === "enquiry" ? "border-[#0052FF] bg-[#EBF2FF] text-[#0052FF]" : "border-[#E4E4E7]"}`}>
                <input type="radio" className="mr-1" checked={form.enquiry_type === "enquiry"} onChange={() => setForm({ ...form, enquiry_type: "enquiry" })} data-testid="lead-type-enquiry" /> Enquiry
              </label>
              <label className={`border rounded-lg p-2 text-sm cursor-pointer text-center ${form.enquiry_type === "loan" ? "border-[#0052FF] bg-[#EBF2FF] text-[#0052FF]" : "border-[#E4E4E7]"}`}>
                <input type="radio" className="mr-1" checked={form.enquiry_type === "loan"} onChange={() => setForm({ ...form, enquiry_type: "loan" })} data-testid="lead-type-loan" /> Loan
              </label>
              <label className={`border rounded-lg p-2 text-sm cursor-pointer text-center ${form.enquiry_type === "insurance" ? "border-[#0052FF] bg-[#EBF2FF] text-[#0052FF]" : "border-[#E4E4E7]"}`}>
                <input type="radio" className="mr-1" checked={form.enquiry_type === "insurance"} onChange={() => setForm({ ...form, enquiry_type: "insurance" })} data-testid="lead-type-insurance" /> Insurance
              </label>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Full name *</Label><Input required value={form.full_name} onChange={(e)=>setForm({...form, full_name: e.target.value})} data-testid="new-lead-name" /></div>
            <div><Label>Phone *</Label><Input required value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} data-testid="new-lead-phone" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} /></div>
            <div><Label>City</Label><Input value={form.city} onChange={(e)=>setForm({...form, city: e.target.value})} /></div>
            {form.enquiry_type === "loan" ? (
              <>
                <div>
                  <Label>Loan type</Label>
                  <Select value={form.loan_type} onValueChange={(v)=>setForm({...form, loan_type: v})}>
                    <SelectTrigger data-testid="new-lead-loan-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{LOAN_PRODUCTS.map(p=><SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Amount (₹)</Label><Input type="number" value={form.loan_amount} onChange={(e)=>setForm({...form, loan_amount: e.target.value})} /></div>
              </>
            ) : form.enquiry_type === "insurance" ? (
              <div className="col-span-2">
                <Label>Insurance type</Label>
                <Select value={form.insurance_type} onValueChange={(v)=>setForm({...form, insurance_type: v})}>
                  <SelectTrigger data-testid="new-lead-ins-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="car">Car</SelectItem>
                    <SelectItem value="bike">Bike</SelectItem>
                    <SelectItem value="cv">Commercial Vehicle</SelectItem>
                    <SelectItem value="health">Health</SelectItem>
                    <SelectItem value="life">Life</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="col-span-2 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-2">
                General Enquiry — no loan or insurance product selected yet. Telecaller can update after follow-up.
              </div>
            )}
            <div className="col-span-2">
              <Label>Source</Label>
              <Select value={form.source} onValueChange={(v)=>setForm({...form, source: v})}>
                <SelectTrigger data-testid="new-lead-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="walk_in">Walk-in</SelectItem>
                  <SelectItem value="website">Website</SelectItem>
                  <SelectItem value="referral">Referral (Employee)</SelectItem>
                  <SelectItem value="telecalling">Telecalling</SelectItem>
                  <SelectItem value="social_media">Social Media</SelectItem>
                  <SelectItem value="bulk_lead">Bulk Lead</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.source === "referral" && (
            <div className="p-3 rounded-lg border border-[#0052FF]/20 bg-[#EBF2FF]/30 space-y-2" data-testid="referral-exec-section">
              <Label>Referral Executive *</Label>
              <Select value={form.referral_executive_id} onValueChange={(v) => setForm({ ...form, referral_executive_id: v })}>
                <SelectTrigger data-testid="referral-exec-select"><SelectValue placeholder="Choose an active employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.employee_id || "—"} · {e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {referredEmp && (
                <div className="text-xs text-[#52525B] grid grid-cols-2 gap-1 mt-2" data-testid="referral-exec-details">
                  <div>Employee ID: <span className="font-mono-num font-semibold text-[#06152D]">{referredEmp.employee_id || "—"}</span></div>
                  <div>Name: <span className="text-[#06152D]">{referredEmp.name}</span></div>
                  <div>Mobile: <span className="font-mono-num text-[#06152D]">{referredEmp.phone || "—"}</span></div>
                  <div>Branch: <span className="text-[#06152D]">{referredEmp.branch || "—"}</span></div>
                </div>
              )}
            </div>
          )}
          <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} /></div>
          <button type="submit" disabled={mut.isPending} className="w-full bg-[#0052FF] hover:bg-[#003FCC] text-white font-semibold px-4 py-2.5 rounded-full disabled:opacity-60" data-testid="new-lead-submit">
            {mut.isPending ? "Saving…" : "Create Lead"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function StatusUpdate({ lead }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: async (status) => (await api.patch(`/leads/${lead.id}`, { status })).data,
    onSuccess: () => { toast.success("Status updated"); qc.invalidateQueries({ queryKey: ["leads"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  if (lead.status === "converted") return <Badge className={STATUS_STYLES.converted} variant="secondary">converted</Badge>;
  return (
    <Select value={lead.status} onValueChange={(v) => mut.mutate(v)}>
      <SelectTrigger className={`h-7 w-32 text-xs ${STATUS_STYLES[lead.status] || ""}`} data-testid={`lead-status-${lead.id}`}><SelectValue /></SelectTrigger>
      <SelectContent>
        {STATUSES.filter(s => s !== "converted").map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function ConvertDialog({ lead }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [convType, setConvType] = useState(lead.enquiry_type === "insurance" ? "insurance" : "loan");
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/leads/${lead.id}/convert`, { conversion_type: convType })).data,
    onSuccess: () => { toast.success("Converted to customer"); qc.invalidateQueries({ queryKey: ["leads"] }); qc.invalidateQueries({ queryKey: ["customers"] }); setOpen(false); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  if (lead.status === "converted") return <span className="text-[10px] text-emerald-700 font-semibold">✓ Converted</span>;
  // Only show Convert action for interested/qualified leads (per spec)
  if (!["interested", "qualified", "contacted"].includes(lead.status)) {
    return <span className="text-[10px] text-[#94A3B8] italic">Mark Interested to convert</span>;
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1" data-testid={`convert-${lead.id}`}>
          <UserCheck size={12} /> Convert
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle>Convert lead → customer</DialogTitle></DialogHeader>
        <div className="text-sm text-[#52525B]">Customer ID will be auto-generated (e.g. SDPTID00042).</div>
        <div className="mt-4 space-y-2">
          {[
            ["loan", "Loan customer only"],
            ["insurance", "Insurance customer only"],
            ["both", "Both loan & insurance customer"],
          ].map(([v, l]) => (
            <label key={v} className={`flex items-center gap-3 border rounded-xl p-3 cursor-pointer ${convType === v ? "border-[#0052FF] bg-[#EBF2FF]" : "border-[#E4E4E7]"}`}>
              <input type="radio" checked={convType === v} onChange={() => setConvType(v)} data-testid={`convert-radio-${v}`} />
              <span className="text-sm font-medium text-[#06152D]">{l}</span>
            </label>
          ))}
        </div>
        <button onClick={() => mut.mutate()} disabled={mut.isPending} className="mt-4 w-full bg-[#0052FF] hover:bg-[#003FCC] text-white font-semibold px-4 py-2.5 rounded-full disabled:opacity-60" data-testid="convert-submit">
          {mut.isPending ? "Converting…" : "Convert & Create Customer"}
        </button>
      </DialogContent>
    </Dialog>
  );
}

function BulkImportDialog() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose an Excel/CSV file"); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/excel/import/leads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      qc.invalidateQueries({ queryKey: ["leads"] });
      toast.success(`Imported ${data.imported} · Skipped ${data.skipped} · Duplicates ${data.duplicates_count}`);
      setFile(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setResult(null); setFile(null); } }}>
      <DialogTrigger asChild>
        <button className="border border-[#0052FF] text-[#0052FF] hover:bg-[#EBF2FF] text-sm font-semibold px-4 py-2 rounded-full inline-flex items-center gap-1.5" data-testid="bulk-import-btn">
          <Upload size={14} /> Bulk Lead Import
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader><DialogTitle>Bulk Lead Import</DialogTitle></DialogHeader>
        <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2 inline-flex items-start gap-1">
          <AlertCircle size={12} className="mt-0.5 shrink-0" />
          <span>All imported records → <b>Source: Bulk Lead</b>, <b>Status: Enquiry</b>. Duplicates (by mobile) auto-detected.</span>
        </div>
        <p className="text-xs text-[#52525B] mt-2">Required columns: <code className="text-[10px] bg-[#F4F4F5] px-1 rounded">full_name, phone</code>. Optional: <code className="text-[10px] bg-[#F4F4F5] px-1 rounded">loan_type, email, city, loan_amount</code>.</p>
        <form onSubmit={submit} className="space-y-3 mt-3" data-testid="bulk-import-form">
          <Input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setFile(e.target.files?.[0])} data-testid="bulk-import-file" />
          <button type="submit" disabled={busy} className="w-full bg-[#0052FF] hover:bg-[#003FCC] text-white font-semibold px-4 py-2.5 rounded-full disabled:opacity-60" data-testid="bulk-import-submit">
            {busy ? "Importing…" : "Import Leads"}
          </button>
        </form>
        {result && (
          <div className="mt-3 grid grid-cols-3 gap-2" data-testid="bulk-import-result">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-center">
              <div className="text-xl font-bold font-mono-num text-emerald-800" data-testid="bi-imported">{result.imported}</div>
              <div className="text-[10px] uppercase tracking-wider text-emerald-700">Imported</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-center">
              <div className="text-xl font-bold font-mono-num text-amber-800" data-testid="bi-skipped">{result.skipped}</div>
              <div className="text-[10px] uppercase tracking-wider text-amber-700">Skipped</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-center">
              <div className="text-xl font-bold font-mono-num text-red-800" data-testid="bi-duplicates">{result.duplicates_count}</div>
              <div className="text-[10px] uppercase tracking-wider text-red-700">Duplicates</div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

async function exportLeads() {
  try {
    const res = await api.get("/excel/export/leads", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `loanjunction_leads_${new Date().toISOString().slice(0,10)}.xlsx`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Leads exported");
  } catch (e) { toast.error("Export failed"); }
}

export default function Leads() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [enquiryType, setEnquiryType] = useState("");
  const { data: leads = [] } = useQuery({
    queryKey: ["leads", q, status, enquiryType],
    queryFn: async () => (await api.get("/leads", { params: { q: q || undefined, status: status || undefined, enquiry_type: enquiryType || undefined } })).data,
  });

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#06152D]">Leads</h1>
          <p className="text-sm text-[#52525B]">Track every enquiry from creation to conversion. Convert Interested leads → Customer.</p>
        </div>
        <div className="flex items-center gap-2">
          <BulkImportDialog />
          <button onClick={exportLeads} className="border border-[#E4E4E7] hover:border-[#0052FF] text-[#06152D] text-sm font-semibold px-4 py-2 rounded-full inline-flex items-center gap-1.5" data-testid="export-leads-btn">
            <Download size={14} /> Export All to Excel
          </button>
          <NewLeadDialog />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
          <Input placeholder="Search name / phone / lead ID" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="leads-search" />
        </div>
        <Select value={enquiryType || "all"} onValueChange={(v) => setEnquiryType(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="leads-enquiry-filter"><SelectValue placeholder="All Enquiries" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Enquiries</SelectItem>
            <SelectItem value="loan">Loan</SelectItem>
            <SelectItem value="insurance">Insurance</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44" data-testid="leads-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 bg-white border border-[#E4E4E7] rounded-xl overflow-hidden">
        <table className="w-full lj-table" data-testid="leads-table">
          <thead>
            <tr>
              <th>Lead ID</th><th>Name</th><th>Phone</th><th>Enquiry</th><th>Type/Amount</th>
              <th>Status</th><th>Source</th><th>Verified</th><th>Created</th><th>Action</th>
            </tr>
          </thead>
          <tbody>
            {leads.length === 0 && <tr><td colSpan="10" className="text-center text-[#52525B] py-8">No leads yet.</td></tr>}
            {leads.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.id}`}>
                <td className="font-mono-num text-xs font-semibold text-[#06152D]">{l.lead_id}</td>
                <td className="font-medium text-[#09090b]">{l.full_name}</td>
                <td className="font-mono-num text-[#52525B]">{l.phone}</td>
                <td><Badge variant="secondary" className={l.enquiry_type === "insurance" ? "bg-violet-100 text-violet-800" : "bg-blue-100 text-blue-800"}>{l.enquiry_type || "loan"}</Badge></td>
                <td className="text-xs">
                  {l.enquiry_type === "insurance" ? (l.insurance_type || "—") : (LOAN_TYPE_LABEL[l.loan_type] || "—")}
                  {l.loan_amount ? <div className="text-[10px] font-mono-num text-[#52525B]">{inr(l.loan_amount)}</div> : null}
                </td>
                <td><StatusUpdate lead={l} /></td>
                <td className="text-xs text-[#52525B]">{l.source}</td>
                <td>{l.mobile_verified ? <Badge className="bg-emerald-100 text-emerald-800 text-[10px]" variant="secondary">verified</Badge> : <span className="text-[10px] text-[#94A3B8]">—</span>}</td>
                <td className="text-xs text-[#52525B]">{new Date(l.created_at).toLocaleDateString("en-IN")}</td>
                <td><ConvertDialog lead={l} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
